import { SxProps } from '@mui/material';
import { CUSTOM_RANGE_LABEL } from 'components/common/DatePicker/DateRangePicker';
import { IconOutlineCalendar1 } from 'components/icons/components/outline/IconOutlineCalendar1';
import {
  NestedFiltersDateRangeType,
  NestedFiltersMenuView,
} from 'features/nestedFilters';
import { generateDateRangeOptions } from 'features/socialListeningCreatorProfile';
import moment from 'moment';
import { NestedFiltersValues } from 'pages/socialMediaListening/community/Filters';
import { useMemo } from 'react';

export type SocialMediaListeningFilterByDateRangeButtonProps = {
  selectedDateRange: {
    label?: string;
    startDate: Date;
    endDate: Date;
  };
  onChange: (params: {
    dateRange: (Date | null)[];
    dateRangeLabel: string;
  }) => void;
  componentsProps?: {
    sx?: SxProps;
    startIcon?: React.ComponentType<{ size: number }>;
    popoverSx?: SxProps;
    showChevronIcon?: boolean;
  };
};

export const SocialMediaListeningFilterByDateRangeButton = (
  props: SocialMediaListeningFilterByDateRangeButtonProps,
) => {
  const { selectedDateRange, onChange, componentsProps = {} } = props;

  const startDate = selectedDateRange?.startDate!;
  const endDate = selectedDateRange?.endDate!;
  const dateRangeLabel = selectedDateRange?.label;

  const predefinedOptions: NestedFiltersDateRangeType = useMemo(
    () => ({
      type: 'date-range',
      key: 'dateRange',
      options: generateDateRangeOptions(),
    }),
    [],
  );

  const selectedPredefinedOptionIndex = useMemo(() => {
    return predefinedOptions.options.findIndex((option) => {
      const sameLabel = dateRangeLabel === option.label;
      const sameRange =
        moment(startDate).isSame(option.startDate, 'day') &&
        moment(endDate).isSame(option.endDate, 'day');

      return sameLabel || sameRange;
    });
  }, [startDate, endDate, predefinedOptions, dateRangeLabel]);

  const hasSelectedCustomRange = Boolean(
    startDate && endDate && selectedPredefinedOptionIndex === -1,
  );

  return (
    <NestedFiltersMenuView
      values={{
        dateRange: {
          value: {
            startDate,
            endDate,
            label: hasSelectedCustomRange
              ? CUSTOM_RANGE_LABEL
              : predefinedOptions.options[selectedPredefinedOptionIndex]
                  ?.label || '',
          },
        },
      }}
      onChange={(_values) => {
        const values = _values as NestedFiltersValues;
        if (!values.dateRange.value) return;
        onChange({
          dateRange: [
            values.dateRange.value.startDate,
            values.dateRange.value.endDate,
          ],
          dateRangeLabel: values.dateRange.value.label,
        });
      }}
      componentsProps={{
        trigger: {
          text: hasSelectedCustomRange
            ? `
              ${startDate ? moment(startDate).format('MMM D, YYYY') : 'From'} - 
              ${endDate ? moment(endDate).format('MMM D, YYYY') : 'To'}
            `
            : predefinedOptions.options[selectedPredefinedOptionIndex]?.label,
          Icon: componentsProps?.startIcon || IconOutlineCalendar1,
          sx: componentsProps?.sx,
          showChevronIcon: componentsProps?.showChevronIcon,
        },
        menu: {
          sx: componentsProps?.popoverSx,
        },
      }}
      items={[predefinedOptions]}
    />
  );
};
